(() => {
  if (customElements.get('l2k-home-calendar')) return;
  const dateKey = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  const fromParts = (parts) => new Date(parts[0], parts[1] - 1, parts[2], parts[3] || 0, parts[4] || 0);
  const monthFormat = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' });
  const dayFormat = new Intl.DateTimeFormat('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  const timeFormat = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' });
  const element = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };
  class HomeCalendar extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      const today = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York' }));
      this.now = today;
      this.todayKey = dateKey(today);
      this.month = new Date(today.getFullYear(), today.getMonth(), 1);
      this.querySelector('[data-previous]').addEventListener('click', () => this.move(-1));
      this.querySelector('[data-next]').addEventListener('click', () => this.move(1));
      this.querySelector('[data-today]').addEventListener('click', () => {
        this.month = new Date(today.getFullYear(), today.getMonth(), 1);
        this.load();
      });
      this.querySelector('[data-filter]').addEventListener('change', () => this.render());
      this.load();
    }
    disconnectedCallback() { this.controller?.abort(); }
    move(delta) {
      this.month = new Date(this.month.getFullYear(), this.month.getMonth() + delta, 1);
      this.load();
    }
    async load() {
      this.controller?.abort();
      this.controller = new AbortController();
      const controller = this.controller;
      const timeout = setTimeout(() => controller.abort(), 12000);
      this.events = null;
      this.querySelector('[data-filter]').disabled = true;
      this.querySelector('[data-month]').textContent = monthFormat.format(this.month);
      this.querySelector('[data-status]').textContent = 'Loading calendar…';
      this.querySelector('[data-calendar]').replaceChildren();
      this.setAttribute('aria-busy', 'true');
      const url = new URL(this.dataset.source);
      url.search = new URLSearchParams({
        start: dateKey(this.month),
        end: dateKey(new Date(this.month.getFullYear(), this.month.getMonth() + 1, 1)),
        show_all_course_occurrences: 'true', calendar: 'true'
      });
      try {
        const response = await fetch(url, { signal: controller.signal, credentials: 'omit' });
        if (!response.ok) throw new Error('Calendar unavailable');
        const data = await response.json();
        if (controller !== this.controller || controller.signal.aborted || !this.isConnected) return;
        if (!Array.isArray(data.schedule)) throw new Error('Invalid calendar');
        this.events = data.schedule.filter((event) => Array.isArray(event.start) && Array.isArray(event.end) && event.title && event.handle);
        this.events.sort((a, b) => fromParts(a.start) - fromParts(b.start));
        const filter = this.querySelector('[data-filter]');
        const selected = filter.value;
        const choices = new Map(this.events.map((event) => [event.handle, event.title]));
        filter.replaceChildren(new Option('All classes & events', ''), ...Array.from(choices, ([value, label]) => new Option(label, value)));
        filter.value = choices.has(selected) ? selected : '';
        filter.disabled = false;
        this.render();
      } catch (error) {
        if (controller !== this.controller || !this.isConnected) return;
        const status = this.querySelector('[data-status]');
        status.replaceChildren(document.createTextNode('Calendar unavailable. '));
        const link = element('a', '', 'View classes & lessons');
        link.href = '/pages/classes-at-luv2knit-more#class-calendar';
        status.append(link);
      } finally {
        clearTimeout(timeout);
        if (controller === this.controller) this.removeAttribute('aria-busy');
      }
    }
    render() {
      if (!this.events) return;
      const selected = this.querySelector('[data-filter]').value;
      const events = this.events.filter((event) => !selected || event.handle === selected);
      const grid = element('div', 'l2k-home-calendar__grid');
      grid.setAttribute('aria-label', monthFormat.format(this.month));
      ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].forEach((day) => {
        const heading = element('div', 'l2k-home-calendar__weekday', day);
        heading.setAttribute('aria-hidden', 'true');
        grid.append(heading);
      });
      const first = new Date(this.month);
      first.setDate(1 - first.getDay());
      const last = new Date(this.month.getFullYear(), this.month.getMonth() + 1, 0);
      last.setDate(last.getDate() + 6 - last.getDay());
      for (let date = new Date(first); date <= last; date.setDate(date.getDate() + 1)) {
        const key = dateKey(date);
        const dayEvents = events.filter((event) => dateKey(fromParts(event.start)) === key);
        const cell = element('div', 'l2k-home-calendar__day');
        if (date.getMonth() !== this.month.getMonth()) cell.classList.add('l2k-home-calendar__day--outside');
        if (!dayEvents.length) cell.classList.add('l2k-home-calendar__day--empty');
        if (key === this.todayKey) cell.setAttribute('aria-current', 'date');
        const label = element('time', 'l2k-home-calendar__date');
        label.dateTime = key;
        const short = element('span', '', String(date.getDate()));
        short.className = 'l2k-home-calendar__date-short';
        short.setAttribute('aria-hidden', 'true');
        const full = element('span', 'l2k-home-calendar__date-full', dayFormat.format(date));
        label.append(short, full);
        cell.append(label);
        dayEvents.forEach((event) => cell.append(this.eventLink(event)));
        grid.append(cell);
      }
      this.querySelector('[data-calendar]').replaceChildren(grid);
      this.querySelector('[data-status]').textContent = events.length ? '' : 'No events scheduled this month.';
    }
    eventLink(event) {
      const past = fromParts(event.end) < this.now;
      const link = element(past ? 'div' : 'a', 'l2k-home-calendar__event');
      const url = new URL(event.url || `/products/${encodeURIComponent(event.handle)}`, location.origin);
      // Schedule links always return to this shop, including full courses' product waitlists.
      if (!past) link.href = url.pathname.startsWith('/products/') ? url.pathname + url.search : '/pages/classes-at-luv2knit-more';
      const full = event.bookedOut === true || event.remainingSpaces === 0;
      if (full) link.classList.add('l2k-home-calendar__event--full');
      link.append(element('span', '', `${timeFormat.format(fromParts(event.start))}–${timeFormat.format(fromParts(event.end))}`));
      link.append(element('strong', '', event.title));
      if (past) link.append(element('span', '', 'Past event'));
      else if (full) link.append(element('span', '', 'Full · View details'));
      else if (Number.isFinite(event.remainingSpaces)) link.append(element('span', '', `${event.remainingSpaces} ${event.remainingSpaces === 1 ? 'space' : 'spaces'} available`));
      if (!past) link.setAttribute('aria-label', `${event.title}, ${dayFormat.format(fromParts(event.start))}, ${event.start[0]}, ${timeFormat.format(fromParts(event.start))} to ${timeFormat.format(fromParts(event.end))} Eastern Time. ${full ? 'Full. View details.' : Number.isFinite(event.remainingSpaces) ? `${event.remainingSpaces} ${event.remainingSpaces === 1 ? 'space' : 'spaces'} available.` : 'View details.'}`);
      return link;
    }
  }
  customElements.define('l2k-home-calendar', HomeCalendar);
})();
